import { RecurrenceRule, WeekDay, MonthlyRecurrenceType } from '@/types/models';

/**
 * Calculate the next occurrence date for a recurring task
 */
export function calculateNextOccurrence(
	baseDate: Date,
	rule: RecurrenceRule,
	occurrenceCount: number = 0,
): Date | null {
	const nextDate = new Date(baseDate);

	switch (rule.type) {
		case 'daily':
			return calculateDaily(nextDate, rule.interval);

		case 'weekly':
			return calculateWeekly(nextDate, rule.interval, rule.daysOfWeek);

		case 'monthly':
			return calculateMonthly(nextDate, rule);

		case 'yearly':
			return calculateYearly(nextDate, rule.interval);

		case 'custom':
			return calculateCustom(nextDate, rule, occurrenceCount);

		default:
			return null;
	}
}

/**
 * Calculate daily recurrence (e.g., "every 3 days")
 */
function calculateDaily(baseDate: Date, interval: number): Date {
	const nextDate = new Date(baseDate);
	nextDate.setDate(nextDate.getDate() + interval);
	return nextDate;
}

/**
 * Calculate weekly recurrence (e.g., "every 2 weeks on Mon, Wed, Fri")
 */
function calculateWeekly(
	baseDate: Date,
	interval: number,
	daysOfWeek?: WeekDay[],
): Date {
	if (!daysOfWeek || daysOfWeek.length === 0) {
		// Default to same day of week if no specific days specified
		const nextDate = new Date(baseDate);
		nextDate.setDate(nextDate.getDate() + (interval * 7));
		return nextDate;
	}

	// Find the next occurrence in the specified days
	const currentDay = baseDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
	const weekDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
	
	const targetDays = daysOfWeek.map(day => weekDays.indexOf(day)).sort((a, b) => a - b);
	
	// Find the next day of week that occurs after the current date
	let nextDay = targetDays.find(day => day > currentDay);
	
	if (nextDay === undefined) {
		// No days left this week, go to next occurrence interval
		nextDay = targetDays[0];
		const nextDate = new Date(baseDate);
		nextDate.setDate(nextDate.getDate() + (interval * 7) + (nextDay - currentDay));
		return nextDate;
	} else {
		// Next occurrence is this week
		const nextDate = new Date(baseDate);
		nextDate.setDate(nextDate.getDate() + (nextDay - currentDay));
		return nextDate;
	}
}

/**
 * Calculate monthly recurrence (e.g., "15th of every month" or "second Tuesday of every month")
 */
function calculateMonthly(baseDate: Date, rule: RecurrenceRule): Date {
	const nextDate = new Date(baseDate);
	
	if (rule.monthlyType === MonthlyRecurrenceType.ByDate && rule.dayOfMonth) {
		// Simple date-based recurrence (e.g., "15th of every month")
		nextDate.setMonth(nextDate.getMonth() + rule.interval);
		nextDate.setDate(Math.min(rule.dayOfMonth, getDaysInMonth(nextDate.getFullYear(), nextDate.getMonth())));
		return nextDate;
	} else if (rule.monthlyType === MonthlyRecurrenceType.ByDay && rule.weekOfMonth && rule.dayOfWeekForMonth) {
		// Day-of-month based recurrence (e.g., "second Tuesday of every month")
		nextDate.setMonth(nextDate.getMonth() + rule.interval);
		return findNthWeekdayOfMonth(nextDate.getFullYear(), nextDate.getMonth(), rule.dayOfWeekForMonth, rule.weekOfMonth);
	}
	
	// Fallback to simple monthly recurrence
	nextDate.setMonth(nextDate.getMonth() + rule.interval);
	return nextDate;
}

/**
 * Calculate yearly recurrence (e.g., "every 3 years")
 */
function calculateYearly(baseDate: Date, interval: number): Date {
	const nextDate = new Date(baseDate);
	nextDate.setFullYear(nextDate.getFullYear() + interval);
	return nextDate;
}

/**
 * Calculate custom recurrence patterns
 */
function calculateCustom(baseDate: Date, rule: RecurrenceRule, occurrenceCount: number): Date | null {
	// This can be extended for complex patterns like "every 3 weeks but only on weekdays"
	// For now, fall back to the basic types
	switch (rule.interval) {
		case 1:
			return calculateDaily(baseDate, 7); // Weekly
		case 2:
			return calculateDaily(baseDate, 14); // Bi-weekly
		case 3:
			return calculateDaily(baseDate, 21); // Every 3 weeks
		case 4:
			return calculateDaily(baseDate, 28); // Every 4 weeks
		default:
			return calculateDaily(baseDate, rule.interval * 7);
	}
}

/**
 * Check if a date should have a recurring task created
 */
export function shouldCreateTask(
	currentDate: Date,
	rule: RecurrenceRule,
	lastCreatedDate?: Date,
	nextDueDate?: Date,
): boolean {
	if (!nextDueDate) return true;
	
	return currentDate >= nextDueDate && !hasEnded(rule, lastCreatedDate);
}

/**
 * Check if the recurrence should end
 */
export function hasEnded(rule: RecurrenceRule, lastCreatedDate?: Date): boolean {
	if (rule.endDate && lastCreatedDate && lastCreatedDate.getTime() >= rule.endDate) {
		return true;
	}
	
	if (rule.maxOccurrences && lastCreatedDate) {
		// This would need the occurrence count from the database
		// For now, return false
		return false;
	}
	
	return false;
}

/**
 * Get the number of days in a month
 */
function getDaysInMonth(year: number, month: number): number {
	return new Date(year, month + 1, 0).getDate();
}

/**
 * Find the nth weekday of a month (e.g., 2nd Tuesday)
 */
function findNthWeekdayOfMonth(
	year: number,
	month: number,
	weekday: WeekDay,
	nth: number,
): Date {
	const weekDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
	const targetDay = weekDays.indexOf(weekday);
	
	const firstDay = new Date(year, month, 1);
	const firstOccurrence = findNextWeekday(firstDay, targetDay);
	
	if (nth === 5) {
		// Find the last occurrence
		const lastOccurrence = new Date(firstOccurrence);
		lastOccurrence.setDate(lastOccurrence.getDate() + 4 * 7); // Add 4 weeks
		
		// If this goes into the next month, step back one week
		if (lastOccurrence.getMonth() !== month) {
			lastOccurrence.setDate(lastOccurrence.getDate() - 7);
		}
		
		return lastOccurrence;
	} else {
		const nthOccurrence = new Date(firstOccurrence);
		nthOccurrence.setDate(nthOccurrence.getDate() + (nth - 1) * 7);
		return nthOccurrence;
	}
}

/**
 * Find the next occurrence of a specific weekday
 */
function findNextWeekday(date: Date, targetDay: number): Date {
	const currentDay = date.getDay();
	const daysToAdd = (targetDay - currentDay + 7) % 7;
	
	const nextDate = new Date(date);
	nextDate.setDate(nextDate.getDate() + daysToAdd);
	return nextDate;
}

/**
 * Generate human-readable description of recurrence rule
 */
export function describeRecurrence(rule: RecurrenceRule): string {
	switch (rule.type) {
		case 'daily':
			return rule.interval === 1 ? 'Daily' : `Every ${rule.interval} days`;
		
		case 'weekly':
			if (rule.daysOfWeek && rule.daysOfWeek.length > 0) {
				const days = rule.daysOfWeek.map(day => 
					day.charAt(0).toUpperCase() + day.slice(1).slice(0, 3),
				).join(', ');
				return rule.interval === 1 ? `Weekly on ${days}` : `Every ${rule.interval} weeks on ${days}`;
			}
			return rule.interval === 1 ? 'Weekly' : `Every ${rule.interval} weeks`;
		
		case 'monthly':
			if (rule.monthlyType === MonthlyRecurrenceType.ByDate && rule.dayOfMonth) {
				return rule.interval === 1 
					? `Monthly on the ${rule.dayOfMonth}${getOrdinalSuffix(rule.dayOfMonth)}`
					: `Every ${rule.interval} months on the ${rule.dayOfMonth}${getOrdinalSuffix(rule.dayOfMonth)}`;
			} else if (rule.monthlyType === MonthlyRecurrenceType.ByDay && rule.weekOfMonth && rule.dayOfWeekForMonth) {
				const ordinal = getOrdinalSuffix(rule.weekOfMonth);
				const day = rule.dayOfWeekForMonth.charAt(0).toUpperCase() + rule.dayOfWeekForMonth.slice(1);
				return rule.interval === 1 
					? `Monthly on the ${ordinal} ${day}`
					: `Every ${rule.interval} months on the ${ordinal} ${day}`;
			}
			return rule.interval === 1 ? 'Monthly' : `Every ${rule.interval} months`;
		
		case 'yearly':
			return rule.interval === 1 ? 'Yearly' : `Every ${rule.interval} years`;
		
		case 'custom':
			return `Custom (every ${rule.interval} weeks)`;
		
		default:
			return 'Does not repeat';
	}
}

/**
 * Get ordinal suffix for numbers (1st, 2nd, 3rd, 4th, etc.)
 */
function getOrdinalSuffix(n: number): string {
	const suffixes = ['th', 'st', 'nd', 'rd'];
	const v = n % 100;
	return suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
}